// @ts-strict-ignore
import { BaseAuthedApiRequest } from '../api';
import {
    Release,
    ReleaseType,
    WorkflowType,
    ChecklistItemStatusType,
    ReleaseEntity,
    AppStoreReviewSubmissionItemType,
    IntegrationInstallationId,
    ReleaseTag,
    ReleaseStepType,
    RollbackResigningStrategy,
} from '../types';
import { ReleaseEntityRequest, AppEntityRequest } from './entities';
import { ToggleFeatureFlagRequest } from './featureFlags';
import { PaginationBaseRequest } from './pagination';
import { ReleaseUpdatable } from './updatable';
import {
    AddIndividualTesterToBuildRequest,
    AssignBetaGroupsToBuildRequest,
    EndBetaSoakRequest,
    PromoteBuildsToTestingTrackRequest,
    SubmitBetaBuildToReviewRequest,
    UpdateChecklistItemCommentRequest,
    CreateOrUpdateBuildLocalizationsRequest,
    UpdateSelectedBuildsInAppStoreRequest,
    SubmitAppForReviewRequest,
    UpdateAppleAppStoreSettingsRequest,
    UpdateGoogleAppStoreSettingsRequest,
    UpdateReviewDetailsRequest,
    UpdateAndroidPhasedReleaseRequest,
    UpdateApplePhasedReleaseRequest,
    UpdateHuaweiPhasedReleaseRequest,
    TagReleaseRequest,
    UpdatePhasedReleaseRequestBase,
    UpdateMetadataRequest,
    ApproveMetadataRequest,
    ReleaseAppleVersionRequest,
    UpdateScreenshotsApprovalRequest,
    DeleteScreenshotRequest,
    UpdateRegressionStatusRequest,
} from '@/actions/stepRun';

export const FETCH_RELEASE_SUCCESS = 'FETCH_RELEASE_SUCCESS';
export const UPDATE_RELEASE_SUCCESS = 'UPDATE_RELEASE_SUCCESS';
export const UPDATE_RELEASE_FAILURE = 'UPDATE_RELEASE_FAILURE';
export const RELEASE_STATE_UPDATED = 'RELEASE_STATE_UPDATED';
export const TRY_NEW_RELEASE = 'TRY_NEW_RELEASE';
export const NEW_RELEASE_SUCCESS = 'NEW_RELEASE_SUCCESS';
export const RELEASES_UPDATED = 'RELEASES_UPDATED';
export const TRY_UPDATE_RELEASE_ENTITY = 'TRY_UPDATE_RELEASE_ENTITY';
export const UPDATE_RELEASE_ENTITY_SUCCESS = 'UPDATE_RELEASE_ENTITY_SUCCESS';
export const TRY_DELETE_RELEASE_ENTITY = 'TRY_DELETE_RELEASE_ENTITY';
export const DELETE_RELEASE_ENTITY = 'DELETE_RELEASE_ENTITY';
export const DELETE_RELEASES = 'DELETE_RELEASES';
export const FETCH_RELEASES_SUCCESS = 'FETCH_RELEASES_SUCCESS_ACTION';
export const TRIGGER_RELEASE_WORKFLOW_BUILD = 'TRY_TRIGGER_BUILD_WORKFLOW';

export interface BaseUpdateReleaseRequest extends BaseAuthedApiRequest {
    appId: string;
    releaseId: string;
}

export interface SkipReleaseRequest extends BaseUpdateReleaseRequest {
    shouldRollForwardMetadata: boolean;
    shouldDevRejectRelease: boolean;
}

export interface SubmitAppForReviewRequestReleaseStep extends BaseUpdateReleaseRequest, SubmitAppForReviewRequest {}

export interface ReleaseAppleVersionRequestReleaseStep extends BaseUpdateReleaseRequest, ReleaseAppleVersionRequest {}

export interface BumpVersionRequest extends BaseUpdateReleaseRequest {
    bumpOnReleaseBranch: boolean;
    targetVersion?: string;
}

export interface UpdateReleasePilotRequest extends BaseUpdateReleaseRequest {
    releasePilotId: string;
}

export interface UpdateCustomizedReleaseSummaryMessageRequest extends BaseUpdateReleaseRequest {
    message: string;
}

export interface UpdateRegressionStatusRequestReleaseStep
    extends BaseUpdateReleaseRequest,
        UpdateRegressionStatusRequest {}

export interface UpdateScreenshotsApprovalRequestReleaseStep
    extends BaseUpdateReleaseRequest,
        UpdateScreenshotsApprovalRequest {
    userId: string;
}

export interface UpdateMetadataApprovalRequestReleaseStep extends BaseUpdateReleaseRequest, ApproveMetadataRequest {}

export interface UpdateTranslationsApprovalRequest {
    isApproved: boolean;
    userId: string;
}

export interface UpdateReviewSubmissionItemRequest extends BaseUpdateReleaseRequest {
    itemId: string;
    type?: AppStoreReviewSubmissionItemType;
}

export interface UpdateChecklistItemStatusRequest extends BaseUpdateReleaseRequest {
    checklistSettingId: string;
    userId: string;
    status?: ChecklistItemStatusType;
}

export interface UpdateReleaseStepChecklistItemCommentRequest
    extends BaseUpdateReleaseRequest,
        UpdateChecklistItemCommentRequest {
    checklistSettingId: string;
    userId: string;
}

export interface LocalizationUpdateRequest {
    locale: string;
    whatsNew: string;
    keywords: string;
    shortDescription: string;
    promotionalText: string;
    description: string;
    subtitle: string;
    videoUrl: string;
    appName: string;
}

export interface UpdateReleaseNotesRequestReleaseStep extends BaseUpdateReleaseRequest, UpdateMetadataRequest {}

export interface DeleteScreenshotRequestReleaseStep extends BaseUpdateReleaseRequest, DeleteScreenshotRequest {}

export interface DeleteAppPreviewRequest extends BaseUpdateReleaseRequest {
    previewId: string;
    language?: string;
    imageType?: string;
}

export interface PredictUpdateReleaseTypeVersionRequest {
    appId: string;
    releaseId: string;
    releaseType: ReleaseType;
}

export interface ReleaseStepEndBetaSoakRequest extends BaseUpdateReleaseRequest, EndBetaSoakRequest {}

export interface TagReleaseRequestReleaseStep extends BaseUpdateReleaseRequest, TagReleaseRequest {}

export interface UpdateSelectedBuildRequest extends BaseUpdateReleaseRequest, UpdateSelectedBuildsInAppStoreRequest {}

export type UpdateAdditionalSelectedBuildsRequest = UpdateSelectedBuildRequest;

export interface StartWorkflowRequest extends BaseUpdateReleaseRequest {
    workflowType: WorkflowType;
    additionalWorkflowVariables?: Record<string, string>;
}

export interface UpdateAppleAppStoreSettingsRequestForReleaseStep
    extends BaseUpdateReleaseRequest,
        UpdateAppleAppStoreSettingsRequest {}

export interface UpdateGoogleAppStoreReleaseSettingsRequestForReleaseStep
    extends BaseUpdateReleaseRequest,
        UpdateGoogleAppStoreSettingsRequest {}

export type UpdateHuaweiAppStoreReleaseSettingsRequestForReleaseStep =
    UpdateGoogleAppStoreReleaseSettingsRequestForReleaseStep;

export interface UpdatePhasedReleaseRequestBaseReleaseStep
    extends BaseUpdateReleaseRequest,
        UpdatePhasedReleaseRequestBase {}

export interface UpdateGooglePhasedReleaseRequestReleaseStep
    extends BaseUpdateReleaseRequest,
        UpdateAndroidPhasedReleaseRequest {}

export interface UpdateHuaweiPhasedReleaseRequestReleaseStep
    extends BaseUpdateReleaseRequest,
        UpdateHuaweiPhasedReleaseRequest {}

export interface UpdateApplePhasedReleaseRequestReleaseStep
    extends BaseUpdateReleaseRequest,
        UpdateApplePhasedReleaseRequest {}

export type DestinationOptionsAction = 'include' | 'exclude';

export interface DestinationOptions {
    destinationIds: string[];
    action: DestinationOptionsAction;
}

export interface NewReleaseRequest extends BaseAuthedApiRequest {
    appId: string;
    releaseType: CreateEditReleaseType;
    version: string;
    targetKickoffDate?: string;
    targetSubmitDate?: string;
    targetReleaseDate?: string;
    releasePilotId?: string;
    releaseName?: string;
    destinationOptions?: DestinationOptions;
}

export interface CreateHotfixOrRollbackReleaseRequest extends NewReleaseRequest {
    shouldCreateBranch: boolean;
    shouldBumpVersionOnBranch: boolean;
    commitsToCherryPick: string[];
    sourceVersion?: string;
    base?: ReleaseTag;
    resigningStrategy?: RollbackResigningStrategy;
    shouldMatchPreviousPhasedRelease: boolean;
}

export enum CreateEditReleaseType {
    Standard = 'release',
    Hotfix = 'hotfix',
    Rollback = 'rollback',
}

export interface UpdateReleaseRequest extends BaseUpdateReleaseRequest {
    releaseType: CreateEditReleaseType;
    version: string;
    targetKickoffDate?: string;
    targetSubmitDate?: string;
    targetReleaseDate?: string;
    releasePilotId?: string;
    releaseName?: string;
    sourceVersion?: string;
    shouldMatchPreviousPhasedRelease?: boolean;
}

export interface DeleteReleaseRequest extends BaseUpdateReleaseRequest {
    appIdParam: string;
    orgIdParam: string;
}

export interface UpdateReleaseResponse {
    releaseId: string;
    release: Release;
}

export type FetchReleaseResponse = UpdateReleaseResponse;

export interface BaseConnectedRelease {
    appId: string;
    orgId: string;
    appIdParam?: string;
    orgIdParam?: string;
}

export interface ConnectedReleaseResponse extends BaseConnectedRelease {
    release: Release | ReleaseEntity;
}

export interface ConnectedReleasesResponse extends BaseConnectedRelease {
    releases: ReleaseEntity[];
}

export interface UpdateReleasePlanningSummaryRequest extends BaseUpdateReleaseRequest {
    releasePlanningSummary: string;
}

export interface DeleteReleaseEntityPayload {
    orgId: string;
    appId: string;
    releaseId: string;
    appIdParam: string;
    orgIdParam: string;
}

export interface SelectRCBuildRequest extends BaseUpdateReleaseRequest {
    buildHash: string;
}

export interface CreateOrUpdateBetaBuildLocalizationsRequest
    extends CreateOrUpdateBuildLocalizationsRequest,
        BaseUpdateReleaseRequest {}

export interface AddIndividualTesterToBuildRequestReleaseStep
    extends BaseUpdateReleaseRequest,
        AddIndividualTesterToBuildRequest {}

export interface AddBetaGroupsToBuildRequest extends AssignBetaGroupsToBuildRequest, BaseUpdateReleaseRequest {}

export interface SubmitBetaBuildForReviewRequestReleaseStep
    extends BaseUpdateReleaseRequest,
        SubmitBetaBuildToReviewRequest {}

export interface PromoteToTestingTrackRequestReleaseStep
    extends BaseUpdateReleaseRequest,
        PromoteBuildsToTestingTrackRequest {}

export interface RetryAppStoreArtifactUploadRequest extends BaseUpdateReleaseRequest {
    workflowRunId: string;
    artifactId: string;
    destinationInstallationId: IntegrationInstallationId;
}

export interface RetryDsymUploadRequest extends BaseUpdateReleaseRequest {
    workflowRunId: string;
    artifactId: string;
    stabilityMonitoringInstallationId?: IntegrationInstallationId;
}

export interface UploadReviewAttachmentRequestForReleaseStep extends BaseUpdateReleaseRequest {
    file: File;
}

export interface UpdateReviewDetailsRequestForReleaseSteps
    extends BaseUpdateReleaseRequest,
        UpdateReviewDetailsRequest {}

export interface GetTimelineEventsHistoryRequest extends Partial<ReleaseEntityRequest>, PaginationBaseRequest {
    orgId: string;
    appId: string;
    releaseStepType?: ReleaseStepType;
}

export interface GetBuildDetailsRequest extends AppEntityRequest {
    releaseId: string;
    buildDetailsSecret: string;
}

export interface StartResignSequenceRequest extends BaseUpdateReleaseRequest {
    signingKeyId?: string;
}

export type UpdateReleaseRequestDataType =
    | BaseUpdateReleaseRequest
    | UpdateReleasePilotRequest
    | UpdateRegressionStatusRequestReleaseStep
    | UpdateScreenshotsApprovalRequestReleaseStep
    | DeleteScreenshotRequestReleaseStep
    | UpdateMetadataApprovalRequestReleaseStep
    | UpdateChecklistItemStatusRequest
    | UpdateReleaseStepChecklistItemCommentRequest
    | ReleaseStepEndBetaSoakRequest
    | TagReleaseRequestReleaseStep
    | UpdateReleaseNotesRequestReleaseStep
    | UpdateReleasePlanningSummaryRequest
    | UpdateCustomizedReleaseSummaryMessageRequest
    | UpdateSelectedBuildRequest
    | UpdateAdditionalSelectedBuildsRequest
    | UpdateAppleAppStoreSettingsRequestForReleaseStep
    | UpdateGoogleAppStoreReleaseSettingsRequestForReleaseStep
    | StartWorkflowRequest
    | CreateOrUpdateBetaBuildLocalizationsRequest
    | AddIndividualTesterToBuildRequestReleaseStep
    | AddBetaGroupsToBuildRequest
    | SubmitBetaBuildForReviewRequestReleaseStep
    | PromoteToTestingTrackRequestReleaseStep
    | ToggleFeatureFlagRequest
    | RetryAppStoreArtifactUploadRequest
    | StartResignSequenceRequest
    | UpdateReviewDetailsRequestForReleaseSteps
    | UploadReviewAttachmentRequestForReleaseStep
    | RetryDsymUploadRequest
    | UpdateReviewSubmissionItemRequest;

interface UpdateReleaseRequestType {
    requestData: UpdateReleaseRequestDataType;
    id?: number | string;
}

export interface ReleaseEntityPayloadType {
    orgId: string;
    orgIdParam: string;
    appId: string;
    appIdParam: string;
}

export interface NewReleasePayloadType extends ReleaseEntityPayloadType {
    request: NewReleaseRequest;
    isRollbackConversion: boolean;
    releaseId?: string;
}

export interface UpdateReleasePayloadType extends ReleaseEntityPayloadType {
    request: UpdateReleaseRequest;
}

export interface TriggerReleaseWorkflowBuildAction {
    type: typeof TRIGGER_RELEASE_WORKFLOW_BUILD;
    payload: StartWorkflowRequest;
}

export const triggerReleaseWorkflowBuild: (payload: StartWorkflowRequest) => TriggerReleaseWorkflowBuildAction = (
    payload,
) => {
    return {
        type: TRIGGER_RELEASE_WORKFLOW_BUILD,
        payload,
    };
};

export interface TryUpdateReleaseAction {
    type: ReleaseUpdatable;
    payload: UpdateReleaseRequestType;
}

export interface FetchReleaseSuccessAction {
    type: typeof FETCH_RELEASE_SUCCESS;
    payload: FetchReleaseResponse;
}

export interface FetchReleasesSuccessAction {
    type: typeof FETCH_RELEASES_SUCCESS;
    payload: ConnectedReleasesResponse;
}

export interface UpdateReleaseSuccessAction {
    type: typeof UPDATE_RELEASE_SUCCESS;
    payload: UpdateReleaseResponse;
}

export interface TryUpdateReleaseEntityAction {
    type: typeof TRY_UPDATE_RELEASE_ENTITY;
    payload: UpdateReleasePayloadType;
}

export interface UpdateReleaseEntitySuccessAction {
    type: typeof UPDATE_RELEASE_ENTITY_SUCCESS;
    payload: ConnectedReleaseResponse;
}

export interface ReleaseStateUpdatedAction {
    type: typeof RELEASE_STATE_UPDATED;
    payload: ConnectedReleaseResponse;
}

export interface ReleasesUpdatedAction {
    type: typeof RELEASES_UPDATED;
    payload: ConnectedReleaseResponse[];
}

export interface TryDeleteReleaseAction {
    type: typeof TRY_DELETE_RELEASE_ENTITY;
    payload: DeleteReleaseRequest;
}

export interface DeleteReleasesAction {
    type: typeof DELETE_RELEASES;
    payload: DeleteReleaseEntityPayload[];
}

export interface TryNewReleaseAction {
    type: typeof TRY_NEW_RELEASE;
    payload: NewReleasePayloadType;
}

export interface NewReleaseSuccessAction {
    type: typeof NEW_RELEASE_SUCCESS;
    payload: ConnectedReleaseResponse;
}

export interface DeleteReleaseEntityAction {
    type: typeof DELETE_RELEASE_ENTITY;
    payload: DeleteReleaseEntityPayload;
}

export const tryUpdateRelease: (
    type: ReleaseUpdatable,
    requestData: UpdateReleaseRequestDataType,
    id?: number | string,
) => TryUpdateReleaseAction = (type, requestData, id = null) => {
    return {
        type: type,
        payload: {
            requestData,
            id,
        },
    };
};

export const fetchReleaseSuccess: (payload: FetchReleaseResponse) => FetchReleaseSuccessAction = (payload) => {
    return {
        type: FETCH_RELEASE_SUCCESS,
        payload,
    };
};

export const fetchReleasesSuccessAction: (payload: ConnectedReleasesResponse) => FetchReleasesSuccessAction = (
    payload,
) => {
    return {
        type: FETCH_RELEASES_SUCCESS,
        payload,
    };
};

export const updateReleaseSuccess: (payload: UpdateReleaseResponse) => UpdateReleaseSuccessAction = (payload) => {
    return {
        type: UPDATE_RELEASE_SUCCESS,
        payload,
    };
};

export const releaseStateUpdatedFromWebsocket: (payload: ConnectedReleaseResponse) => ReleaseStateUpdatedAction = (
    payload,
) => {
    return {
        type: RELEASE_STATE_UPDATED,
        payload,
    };
};

export const tryNewReleaseAction: (payload: NewReleasePayloadType) => TryNewReleaseAction = (payload) => {
    return {
        type: TRY_NEW_RELEASE,
        payload,
    };
};

export const newReleaseSuccessAction: (payload: ConnectedReleaseResponse) => NewReleaseSuccessAction = (payload) => {
    return {
        type: NEW_RELEASE_SUCCESS,
        payload,
    };
};

export const releasesUpdated: (payload: ConnectedReleaseResponse[]) => ReleasesUpdatedAction = (payload) => {
    return {
        type: RELEASES_UPDATED,
        payload,
    };
};

export const deleteReleasesTriggeredFromWebsocket: (payload: DeleteReleaseEntityPayload[]) => DeleteReleasesAction = (
    payload,
) => {
    return {
        type: DELETE_RELEASES,
        payload,
    };
};

// Update release entity - for possibly changing release id / version

export const tryUpdateReleaseEntity: (payload: UpdateReleasePayloadType) => TryUpdateReleaseEntityAction = (
    payload,
) => {
    return {
        type: TRY_UPDATE_RELEASE_ENTITY,
        payload: payload,
    };
};

export const updateReleaseEntitySuccess: (response: ConnectedReleaseResponse) => UpdateReleaseEntitySuccessAction = (
    response,
) => {
    return {
        type: UPDATE_RELEASE_ENTITY_SUCCESS,
        payload: response,
    };
};

export const tryDeleteRelease: (request: DeleteReleaseRequest) => TryDeleteReleaseAction = (request) => {
    return {
        type: TRY_DELETE_RELEASE_ENTITY,
        payload: request,
    };
};

export const deleteReleaseEntity: (payload: DeleteReleaseEntityPayload) => DeleteReleaseEntityAction = (payload) => {
    return {
        type: DELETE_RELEASE_ENTITY,
        payload,
    };
};
